'use client';

import { motion } from 'framer-motion';
import Image from 'next/image';
import { COMPANY_ASSETS } from '@/lib/constants';
import { Image as ImageType } from '@/types/types';
import { SectionContainer } from '../../ui/SectionContainer';

interface SimpleHeroProps {
	heading: string;
	sub_heading: string;
	description: string;
	trust_seal?: ImageType;
	isFirst: boolean;
}

const defaultTrustSeal = {
	url: COMPANY_ASSETS.trustSeal,
	alternativeText: 'Trust Seal',
};

export function SimpleHero({
	heading,
	sub_heading,
	description,
	trust_seal = defaultTrustSeal,
	isFirst,
}: SimpleHeroProps) {
	// Animation variants
	const fadeIn = {
		hidden: { opacity: 0, y: 20 },
		visible: {
			opacity: 1,
			y: 0,
			transition: {
				duration: 0.7,
				ease: 'easeOut',
			},
		},
	};

	const scaleUp = {
		hidden: { opacity: 0, scale: 0.95 },
		visible: {
			opacity: 1,
			scale: 1,
			transition: {
				duration: 0.5,
				ease: 'easeOut',
				delay: 0.3,
			},
		},
	};

	return (
		<SectionContainer className="simple-hero-section">
			<noscript>
				<style>{`
					.simple-hero-section [style*='opacity'],
					.simple-hero-section [style*='translateY'],
					.simple-hero-section [style*='scale'] {
						opacity: 1 !important;
						transform: none !important;
					}
				`}</style>
			</noscript>
			{/* Decorative background elements */}
			<div className="absolute left-0 top-24 -z-10 h-72 w-72 rounded-full bg-blue-100/40 opacity-60 blur-3xl"></div>
			<div className="absolute right-0 top-36 -z-10 h-64 w-64 rounded-full bg-indigo-100/40 opacity-60 blur-3xl"></div>

			<div className="relative mx-auto max-w-4xl px-4 text-center">
				<motion.div
					initial="hidden"
					animate="visible"
					variants={fadeIn}
					className="relative">
					{isFirst && (
						<h1 className="mb-6 text-4xl font-extrabold leading-tight tracking-tight text-gray-900 md:text-5xl lg:text-6xl">
							{heading}
							<span
								className="animate-gradient mt-3 block bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 bg-[length:400%_100%] bg-clip-text text-transparent drop-shadow-sm"
								style={{
									animation: 'gradient 8s ease infinite',
								}}>
								{sub_heading}
							</span>
						</h1>
					)}
					{!isFirst && (
						<h2 className="mb-6 text-4xl font-extrabold leading-tight tracking-tight text-gray-900 md:text-5xl lg:text-6xl">
							{heading}
							<span
								className="animate-gradient mt-3 block bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 bg-[length:400%_100%] bg-clip-text text-transparent drop-shadow-sm"
								style={{
									animation: 'gradient 8s ease infinite',
								}}>
								{sub_heading}
							</span>
						</h2>
					)}
					{/* Custom CSS Animation for the gradient */}
					<style jsx global>{`
						@keyframes gradient {
							0% {
								background-position: 0% 50%;
							}
							50% {
								background-position: 100% 50%;
							}
							100% {
								background-position: 0% 50%;
							}
						}
					`}</style>

					<p className="mx-auto mb-8 max-w-3xl text-lg font-medium leading-relaxed text-gray-600 md:text-xl">
						{description}
					</p>
				</motion.div>

				{trust_seal && (
					<motion.div
						initial="hidden"
						animate="visible"
						variants={scaleUp}
						className="group mx-auto max-w-2xl">
						<div className="relative overflow-hidden rounded-2xl border border-gray-100 bg-white/90 p-5 shadow-lg backdrop-blur-sm transition-all duration-300 hover:border-blue-100 hover:shadow-xl">
							<Image
								src={trust_seal.url}
								alt={
									trust_seal.alternativeText || 'Trust Badges'
								}
								width={768}
								height={223}
								className="relative z-10 h-auto w-full transition-transform duration-500 group-hover:scale-[1.02]"
								priority={isFirst}
							/>
						</div>
					</motion.div>
				)}
			</div>
		</SectionContainer>
	);
}
